import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {environment} from 'environment';
import {AuthRequest} from 'shared/auth-request/auth-request';

import {AnswerType, Question, TipList, TipRating} from '../model/model';

const questionIndex: number = 0;
const HTTP_OK_CODE: number = 200;

interface AnswerData {
  answer: AnswerType;
  questionDate: Date;
  subtree?: string;
}

interface RatingData {
  rating: AnswerType;
}

@Injectable()
export class TipRepository {

  constructor(
    private http: HttpClient,
    private authRequest: AuthRequest,
  ) { }

  public getQuestionChain(name: string): Observable<Question> {
    const route: string = `${environment.questions.startRoute}/${name}`;

    return this.authRequest.handleRequest(
      this.http.get<Question>(route, {withCredentials: true}));
  }

  public getQuestion(questId: string): Observable<Question> {
    const route: string = `${environment.questions.startRoute}/id_${questId}`;

    return this.authRequest.handleRequest(
      this.http.get<Question>(route, {withCredentials: true}));
  }

  public postAnswer(questionId: string, value: AnswerType, questionDate: Date, subtree: string): Observable<Question | TipList> {
    const route: string = `${environment.questions.answerRoute}/${questionId}`;
    const data: AnswerData = {
      answer: value,
      questionDate: questionDate,
    };
    if (subtree) {
      data.subtree = subtree;
    }

    return this.authRequest.handleRequest(
      this.http.post(route, data, {withCredentials: true}),
    ).pipe(map((response) => { return <Question | TipList> response; }));
  }

  public skipReflection(questionId: string, questionDate: Date): Observable<boolean> {
    const route: string = `${environment.reflection.skipReflectionRoute}/${questionId}`;
    const data: {} = {
      questionDate: questionDate,
    };
    return this.authRequest.handleRequest(
      this.http.post(route, data, {observe: 'response', responseType: 'text', withCredentials: true})
      .pipe(map((response) => response.status === HTTP_OK_CODE)));
  }

  public skipOldReflections(): Observable<boolean> {
    const route: string = `${environment.reflection.skipOldReflectionsRoute}`;
    const data: {} = {};
    return this.authRequest.handleRequest(
      this.http.post(route, data, {observe: 'response', responseType: 'text', withCredentials: true})
      .pipe(map((response) => response.status === HTTP_OK_CODE)));
  }

  public postTipRating(tipId: string, value: AnswerType): Observable<boolean> {
    const route: string = `${environment.tips.postRatingRoute}/${tipId}`;
    const data: RatingData = {rating: value};

    return this.authRequest.handleRequest(
      this.http.post(route, data, {observe: 'response', responseType: 'text', withCredentials: true})
      .pipe(map((response) => response.status === HTTP_OK_CODE)));
  }

  public getTipRatings(): Observable<Array<TipRating>> {
    return this.authRequest.handleRequest(
      this.http.get(environment.tips.ratingsRoute, {withCredentials: true}),
    ).pipe(map((response) => { return <Array<TipRating>> response; }));
  }

  public getButtonAges(name: string): Observable<Array<Date>> {
    return this.authRequest.handleRequest(
      this.http.get<Array<Date>>(`${environment.questions.buttonAgesRoute}/${name}`, {withCredentials: true}));
  }

  public getByType(type: string): Observable<Array<any>> {
    return this.authRequest.handleRequest(
      this.http.get(`${environment.tips.byType}/${type}`, {withCredentials: true}),
    ).pipe(map((response) => { return <Array<any>> response; }));
  }

}
